import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import Modal from 'react-modal';
import cx from 'classnames';
import { isEqual } from 'lodash';

import { optimizeImage, cropImageToDataURL, generateImageName, getOrientation } from './utils/imageHelpers';

import * as Icons from '../icons/Icons';
import PhotoCropper from './PhotoCropper';
import PromoteGalleryToHero from './PromoteGalleryToHero';
import UploadProgressIndicator from './UploadProgressIndicator';
import Loading from '../Loading';
import TextField from '../TextField';
import Label from '../Label';
import Button from '../Button';

import { UPDATE, UPLOAD, DELETE, CROP } from './utils/UploadManager';
import { IMAGE_SHAPE, CROPPED_IMAGE_SHAPE, UPLOAD_MANAGER_SHAPE } from './shapes';

/**
 * SinglePhotoManager
 * ----------------------------------------------------------------------------
 * Props:
 *
 * id ................. Used for both dom elements and selecting items from the gallery array
 * imageType .......... Tells the API what type the uploaded image is
 * imageDisplayType .. Used for button labels and modal titles
 * originalImage ...... An object containing the image source and specs
 * croppedImage ....... An object containing the image source and specs
 * altText ............ Populates the alternate text field
 * caption ............ Populates the caption field
 * credit ............. Populates the credit field
 * desiredWidth ....... The optimum width for the cropped image
 * desiredHeight ...... The optimum height for the cropped image
 * uploadManager ...... The instance of UploadManager
 * onPublish .......... Callback after a new photo has been successfully uploaded
 * onUpdate ........... Callback after the altText, caption or credit is successfully updated
 * onCancel ........... Callback after the user cancels editing the photo, altText, caption or credit
 * onDelete ........... Callback after the photo and text has been successfully deleted
 * hasHeroImage ....... [Preview Image] True if a hero image has been published
 * getHeroImage ....... [Preview Image] Function that returns the hero image data
 * numberOfGalleryItems [Gallery Image] The number of published gallery items; used for changing item position
 * position ........... [Gallery Image] The index of the item in the gallery array
 * reorderGallery ..... [Gallery Image] Function that changes the order of the item in the gallery array
 *
 *
 * Shape of originalImage:
 *     {
 *         name: string, the sluggified filename
 *         source: string, dataURL (unpublished), url (published)
 *         width: number, width in pixels
 *         height: number, height in pixels
 *         size: number, size in bytes (only unpublished)
 *         mediaId: string, server-generated UUID (only published)
 *     }
 *
 * Shape of croppedImage:
 *     {
 *         percentCrop: {
 *             x: number, crop origin in percent of original image
 *             y: number: crop origin in percent of original image
 *             height: crop image height as a percent of original image's height
 *             width: crop image width as a percent of original image's width
 *         },
 *         pixelCrop: {
 *             x: number, crop origin in pixels
 *             y: number: crop origin in pixels
 *             height: crop image height in pixels
 *             width: crop image width in pixels
 *         }
 *         name: string, the sluggified filename
 *         source: string, dataURL (unpublished), url (published)
 *         width: number, width in pixels
 *         height: number, height in pixels
 *         size: number, size in bytes (only unpublished)
 *         mediaId: string, server-generated UUID (only published)
 *     }
 */
export default class SinglePhotoManager extends React.PureComponent {
    static propTypes = {
        id: PropTypes.string.isRequired,
        imageType: PropTypes.oneOf(['hero', 'preview', 'gallery']).isRequired,
        imageTypeDisplay: PropTypes.string.isRequired,
        originalImage: IMAGE_SHAPE,
        croppedImage: CROPPED_IMAGE_SHAPE,
        altText: PropTypes.string,
        caption: PropTypes.string,
        credit: PropTypes.string,
        desiredWidth: PropTypes.number,
        desiredHeight: PropTypes.number,
        uploadManager: UPLOAD_MANAGER_SHAPE.isRequired,
        onPublish: PropTypes.func,
        onUpdate: PropTypes.func,
        onCancel: PropTypes.func,
        onDelete: PropTypes.func,

        // For preview image
        hasHeroImage: PropTypes.bool,
        getHeroImage: PropTypes.func,

        // For gallery images
        numberOfGalleryItems: PropTypes.number,
        position: PropTypes.number,
        reorderGallery: PropTypes.func,
        onHeroPromotion: PropTypes.func,
        getIsAltTextUnique: PropTypes.func
    };

    static defaultProps = {
        originalImage: {},
        croppedImage: {},
        altText: '',
        caption: '',
        credit: '',
        desiredWidth: 700,
        desiredHeight: 524,
        hasHeroImage: false,
        numberOfGalleryItems: 0,
        position: 0,
        getHeroImage: () => { },
        onPublish: () => { },
        onUpdate: () => { },
        onDelete: () => { },
        onCancel: () => { },
        reorderGallery: () => { },
        onHeroPromotion: () => { },
        getIsAltTextUnique: () => true
    };

    constructor(props) {
        super(props);

        this.state = {
            dropzoneActive: false,
            isUploading: false,
            isSaving: false,
            isSavingCrop: false,
            isCropping: false,
            isWarningAboutPromoting: false,
            error: '',
            uploadPercentCompleted: 0,
            uploadStatus: '',
            originalImage: this.props.originalImage,
            croppedImage: this.props.croppedImage,
            altText: this.props.altText,
            caption: this.props.caption,
            credit: this.props.credit,
            isAltTextInvalid: false
        };

        this.lastSavedState = {
            originalImage: this.props.originalImage,
            croppedImage: this.props.croppedImage,
            altText: this.props.altText,
            caption: this.props.caption,
            credit: this.props.credit
        };

        this.imageHasBeenPublished = true;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps !== this.props) {
            if (
                (this.state.altText && !nextProps.altText) ||
                (this.state.caption && !nextProps.caption) ||
                isEqual(this.state.originalImage, nextProps.originalImage)) {
                return;
            }

            this.setState({
                originalImage: nextProps.originalImage,
                croppedImage: nextProps.croppedImage,
                altText: nextProps.altText,
                caption: nextProps.caption,
                credit: nextProps.credit
            });

            this.lastSavedState = {
                originalImage: nextProps.originalImage,
                croppedImage: nextProps.croppedImage,
                altText: nextProps.altText,
                caption: nextProps.caption,
                credit: nextProps.credit
            };

            // If this is a preview image, the useHeroImage function will send a
            // new original image and an empty cropped image ... we need to trigger
            // the crop tool.
            if (this.props.imageType === 'preview' && nextProps.originalImage.source && !nextProps.croppedImage.source) {
                this.setState({
                    isCropping: true
                });
            }
        }
    }

    onDragEnter = () => {
        this.setState({
            dropzoneActive: true
        });
    };

    onDragLeave = () => {
        this.setState({
            dropzoneActive: false
        });
    };

    onDropRejected = () => {
        this.setState({
            error: 'Make sure your image is a .jpg, .jpeg, or .png file and try again.'
        });
    };

    onDrop = (acceptedFiles) => {
        this.setState({
            dropzoneActive: false
        });

        // acceptedFiles is always an array, even if only one file was uploaded.
        // Multiples are not allowed, so we grab the first item.
        const file = acceptedFiles[0];

        if (file) {
            // Prevent memory leaks
            window.URL.revokeObjectURL(file.preview);

            const reader = new FileReader();

            reader.onload = (e) => {
                const result = e.target.result;
                const img = new Image();

                img.onload = async () => {
                    const exifOrientation = await getOrientation(result);

                    const droppedImage = {
                        source: result,
                        name: generateImageName(file.name),
                        size: file.size,
                        height: img.height,
                        width: img.width,
                        exifOrientation
                    };

                    // If the dropped image has a long edge longer than 4000px,
                    // we're going to downsize it to avoid crazy big images
                    // from getting uploaded
                    const originalImage = await optimizeImage(droppedImage);
                    let croppedImage = {};

                    // Gallery images don't automatically trigger the cropping tool,
                    // so cropped is equal to original
                    if (this.props.imageType === 'gallery') {
                        croppedImage = {
                            ...originalImage,
                            pixelCrop: {
                                x: 0,
                                y: 0,
                                width: originalImage.width,
                                height: originalImage.height
                            }
                        };
                    }
                    // Image must be loaded to get width and height.
                    // Put new photo in state and clear previous croppedImage

                    this.setState({
                        originalImage,
                        croppedImage,
                        isCropping: this.props.imageType !== 'gallery'
                    });
                };

                img.src = result;
            };

            reader.readAsDataURL(file);
        }
    };

    handleInputChange = (event) => {
        this.setState({
            [event.target.name]: event.target.value
        });
    }

    saveCroppedPhoto = (pixelCrop) => {
        const { originalImage } = this.state;

        if (!this.imageHasBeenPublished) {
            cropImageToDataURL(originalImage, pixelCrop).then((res) => {
                const img = new Image();

                img.onload = () => {
                    this.setState({
                        isCropping: false,
                        croppedImage: {
                            source: res.dataURL,
                            name: generateImageName(originalImage.name, 'jpg', 'cropped'),
                            // approx file size because the Base64 encoding has a 4/3 overhead
                            size: Math.round((res.dataURL.length * 3) / 4),
                            height: img.height,
                            width: img.width,
                            percentCrop: res.cropData,
                            pixelCrop
                        }
                    });
                };

                img.src = res.dataURL;
            });
        }
        else {
            this.setState({
                isCropping: false,
                croppedImage: {
                    ...this.state.croppedImage,
                    pixelCrop
                }
            }, () => {
                this.uploadNewImageCrop();
            });
        }
    };

    editPhotoCrop = (e) => {
        e.preventDefault();

        const { croppedImage, originalImage } = this.state;
        const pixelCrop = croppedImage.pixelCrop;

        if (croppedImage.percentCrop === undefined) {
            const percentCrop = {
                x: pixelCrop.x / originalImage.width * 100,
                y: pixelCrop.y / originalImage.height * 100,
                width: pixelCrop.width / originalImage.width * 100,
                height: pixelCrop.height / originalImage.height * 100
            };

            this.setState({
                isCropping: true,
                croppedImage: {
                    ...this.state.croppedImage,
                    percentCrop
                }
            });
        }
        else {
            this.setState({
                isCropping: true
            });
        }
    };

    movePositionUp = (e) => {
        e.preventDefault();

        const { position, reorderGallery } = this.props;

        if (position > 0) {
            reorderGallery({
                oldIndex: position,
                newIndex: position - 1
            });
        }
    };

    movePositionDown = (e) => {
        e.preventDefault();

        const { position, reorderGallery, numberOfGalleryItems } = this.props;

        if (position < numberOfGalleryItems - 1) {
            reorderGallery({
                oldIndex: position,
                newIndex: position + 1
            });
        }
    };

    deletePhotoAndText = (e) => {
        e.preventDefault();
        const { uploadManager, id } = this.props;
        const mediaId = this.state.croppedImage.mediaId;

        if (mediaId && mediaId.length) {
            const newState = {
                isSaving: false,
                originalImage: {},
                croppedImage: {},
                altText: '',
                caption: '',
                credit: ''
            };

            const onError = (err) => {
                // eslint-disable-next-line no-console
                console.log(err);
                this.setState({
                    error: 'The server had trouble deleting the photo. Please try again.',
                    isSaving: false
                });
            };

            const onCompletion = (res) => {
                if (res && !res.data.err) {
                    this.lastSavedState = newState;

                    this.setState({
                        ...this.state,
                        ...newState
                    });

                    // Envoke callback
                    this.props.onDelete(id);
                }
                else {
                    onError(res.data.err);
                }
            };

            const task = {
                type: DELETE,
                image: this.generatePhotoProperties(),
                onCompletion,
                onError
            };

            this.setState({
                isSaving: true
            });

            // Remove data from the server
            uploadManager.addTask(task);
        }
    };

    publishChanges = (e) => {
        e.preventDefault();

        const { originalImage, croppedImage, altText, caption, credit } = this.state;

        // Abort if all information is not present
        if (!originalImage.source || !croppedImage.source || !altText || !caption || !credit) {
            return;
        }

        if (croppedImage.source.indexOf('https:') === -1) {
            // New image, so upload everything
            this.uploadPhoto();
        }
        else {
            // Image hasn't changed so just upload changes
            this.uploadChanges();
        }
    };

    uploadNewImageCrop = () => {
        const { uploadManager, onUpdate, id } = this.props;
        const { croppedImage } = this.state;
        const mediaId = this.state.croppedImage.mediaId;

        if (mediaId && mediaId.length) {
            const onError = (err) => {
                // eslint-disable-next-line no-console
                console.log(err);
                this.setState({
                    error: 'The server had trouble saving the cropped photo. Please try again.',
                    isSaving: false
                });
            };

            const onCompletion = (res) => {
                if (res && !res.data.err) {
                    this.lastSavedState.croppedImage = this.state.croppedImage;

                    // Timeout to prevent 403 errors
                    setTimeout(() => {
                        // Envoke callback
                        onUpdate({
                            id,
                            ...this.state
                        });
                    }, 2000);
                }
                else {
                    onError(res.data.err);
                }
            };

            const photoProperties = {
                media_id: croppedImage.mediaId,
                crop_x: croppedImage.pixelCrop.x,
                crop_y: croppedImage.pixelCrop.y,
                crop_width: croppedImage.pixelCrop.width,
                crop_height: croppedImage.pixelCrop.height
            };

            const task = {
                type: CROP,
                image: photoProperties,
                onCompletion,
                onError
            };

            this.setState({
                isSavingCrop: true
            });

            // Save data to the server
            uploadManager.addTask(task);
        }
    };

    uploadChanges = () => {
        const { uploadManager, onUpdate, id } = this.props;
        const mediaId = this.state.croppedImage.mediaId;

        if (mediaId && mediaId.length) {
            const onError = (err) => {
                // eslint-disable-next-line no-console
                console.log(err);
                this.setState({
                    error: 'The server had trouble saving your changes. Please try again.',
                    isSaving: false
                });
            };

            const onCompletion = (res) => {
                if (res && !res.data.err) {
                    this.lastSavedState = {
                        originalImage: this.state.originalImage,
                        croppedImage: this.state.croppedImage,
                        altText: this.state.altText,
                        caption: this.state.caption,
                        credit: this.state.credit
                    };

                    this.setState({
                        isSaving: false
                    });

                    // Envoke callback
                    onUpdate({
                        id,
                        ...this.state
                    });
                }
                else {
                    onError(res.data.err);
                }
            };

            const task = {
                type: UPDATE,
                image: this.generatePhotoProperties(),
                onCompletion,
                onError
            };

            this.setState({
                isSaving: true
            });

            // Save data to the server
            uploadManager.addTask(task);
        }
    };

    uploadPhoto = () => {
        const { imageType, uploadManager, position, numberOfGalleryItems } = this.props;
        const { croppedImage, originalImage, altText, caption, credit } = this.state;

        // Bail if file is too large.  This is a last-ditch effort to avoid a
        // 413 error that can't be caught via JavaScript
        if (croppedImage.size > 16000000) {
            this.setState({
                isUploading: false,
                error: 'Your file is too large. Images should be 16MB or less.',
                uploadPercentCompleted: 0,
                uploadStatus: ''
            });

            return;
        }

        // Define onProgress function
        const onProgress = (progressEvent) => {
            const percentCompleted = progressEvent.loaded / progressEvent.total;
            const uploadStatus = `${((progressEvent.total / 1024) * percentCompleted).toFixed(2)} kb of ${(progressEvent.total / 1024).toFixed(2)} kb`;
            const uploadPercentCompleted = Math.round(percentCompleted * 100);



            if (uploadPercentCompleted < 0.85) {
                this.setState({
                    uploadStatus,
                    uploadPercentCompleted: uploadPercentCompleted - 15
                });
            }
            else {
                this.setState({
                    uploadStatus: 'Processing file ...',
                    uploadPercentCompleted: 87
                });
            }
        };

        // Define onCompletion
        const onCompletion = (res) => {
            if (res && !res.data.err) {
                const media = res.data.media;

                this.setState({
                    uploadStatus: 'Finalizing upload ...',
                    uploadPercentCompleted: 92
                });

                // Yay nested setTimeouts!
                // Chaining setStates to manually move the progress bar so it
                // doesn't seem like the upload stalled. The 2+ second delay is
                // so the server-side processes can finish and we can avoid
                // 403 errors when the component updates
                setTimeout(() => {
                    this.setState({
                        uploadStatus: 'Finalizing upload ...',
                        uploadPercentCompleted: 97
                    });

                    setTimeout(() => {
                        this.setState({
                            uploadStatus: 'Success!',
                            uploadPercentCompleted: 100
                        });

                        setTimeout(() => {
                            // Below is some trickery to prevent slow image loads
                            // and 403 errors. We're not going to update the component
                            // with the real URL of the image, and instead going to keep
                            // using the dataURL.
                            //
                            // To do this, we have to add the mediaId to the state
                            // and the lastSavedState
                            this.lastSavedState = {
                                id: this.props.id,
                                altText: media.title,
                                caption: media.description,
                                credit: media.credits,
                                originalImage: {
                                    mediaId: media.media_id,
                                    ...this.state.originalImage
                                },
                                croppedImage: {
                                    mediaId: media.media_id,
                                    ...this.state.croppedImage
                                }
                            };

                            this.setState(
                                {
                                    isUploading: false,
                                    uploadPercentCompleted: 0,
                                    uploadStatus: '',
                                    originalImage: {
                                        mediaId: media.media_id,
                                        ...this.state.originalImage
                                    },
                                    croppedImage: {
                                        mediaId: media.media_id,
                                        ...this.state.croppedImage
                                    }
                                },
                                () => {
                                    const newMediaItem = {
                                        id: this.props.id,
                                        altText: media.title,
                                        caption: media.description,
                                        credit: media.credits,
                                        originalImage: {
                                            mediaId: media.media_id,
                                            source: media.url,
                                            width: parseInt(media.width, 10),
                                            height: parseInt(media.height, 10),
                                            name: media.media_id,
                                            type: media.mime_type
                                        },
                                        croppedImage: {
                                            mediaId: media.media_id,
                                            source: media.url,
                                            width: parseInt(media.width, 10),
                                            height: parseInt(media.height, 10),
                                            name: media.media_id,
                                            type: media.mime_type,
                                            pixelCrop: {
                                                x: media.crop_x,
                                                y: media.crop_y,
                                                width: media.crop_width,
                                                height: media.crop_height
                                            }
                                        }
                                    };

                                    this.props.onPublish(newMediaItem, this.state.originalImage);
                                }
                            );
                        }, 2500);
                    }, 700);
                }, 700);
            }
            else {
                // eslint-disable-next-line no-console
                console.log('uploadPhoto - API completion but res.data.err is truthy', res.data.err);
                this.setState({
                    error: 'The server had trouble saving your photo. Please try again.',
                    uploadPercentCompleted: 0,
                    uploadStatus: ''
                });
            }
        };

        // Define onError
        const onError = (err) => {
            // eslint-disable-next-line no-console
            console.log(('uploadPhoto - API return caught error'), err);
            this.setState({
                isUploading: false,
                error: 'The server had trouble saving your photo. Please try again.',
                uploadPercentCompleted: 0,
                uploadStatus: ''
            });
        };

        // Create task object
        const task = {
            type: UPLOAD,
            image: {
                ...originalImage,
                pixelCrop: croppedImage.pixelCrop,
                altText,
                caption,
                credit,
                type: imageType,
                position: position === -1 ? numberOfGalleryItems : position
            },
            onProgress,
            onCompletion,
            onError
        };



        this.setState({ isUploading: true });

        // Start upload
        uploadManager.addTask(task);

        // Delete original image if this is a replacement
        if (this.props.croppedImage.mediaId) {
            const deleteTask = {
                type: DELETE,
                image: { media_id: this.props.croppedImage.mediaId },
                onCompletion: () => { },
                // eslint-disable-next-line no-console
                onError: (err) => console.warn('replace > delete image error', err)
            };

            uploadManager.addTask(deleteTask);
        }
    };

    useHeroImage = (e) => {
        e.preventDefault();

        const newState = this.props.getHeroImage();

        this.setState({
            ...this.state,
            ...newState,
            isCropping: true
        });
    };

    openFilesInput = (e) => {
        e.preventDefault();
        const input = document.getElementById(`photo-files-input-${this.props.id}`);
        input.value = '';
        input.click();
    };

    cancelCrop = (e) => {
        e.preventDefault();

        // For hero and preview images, canceling means dumping the dragged image entirely
        if (this.lastSavedState.originalImage.source && !this.lastSavedState.croppedImage.source) {
            this.lastSavedState.originalImage = {};
            this.lastSavedState.croppedImage = {};
        }

        this.setState({
            isCropping: false,
            originalImage: this.lastSavedState.originalImage,
            croppedImage: this.lastSavedState.croppedImage
        });
    };

    cancelEditing = (e) => {
        e.preventDefault();

        const { onCancel, id } = this.props;

        this.setState({
            ...this.state,
            ...this.lastSavedState
        });

        // Envoke callback
        onCancel({
            id,
            ...this.state
        });
    };

    clearError = () => {
        this.setState({
            error: ''
        });
    };

    generatePhotoProperties() {
        const { croppedImage, altText, caption, credit } = this.state;
        return {
            media_id: croppedImage.mediaId,
            url: croppedImage.source,
            title: altText,
            description: caption,
            credits: credit,
            is_primary: this.props.imageType === 'hero',
            is_preview: this.props.imageType === 'preview',
            is_gallery: this.props.imageType === 'gallery',
            crop_x: croppedImage.pixelCrop.x,
            crop_y: croppedImage.pixelCrop.y,
            crop_width: croppedImage.pixelCrop.width,
            crop_height: croppedImage.pixelCrop.height,
            position: this.props.position
        };
    }

    toggleWarningAboutPromoting = () => {
        this.setState({
            isWarningAboutPromoting: !this.state.isWarningAboutPromoting
        });
    }

    handleNewErrorState = ({ isInvalid }) => {
        this.setState({
            isAltTextInvalid: isInvalid
        });
    }

    altTextValidator = (input) => {
        const { getIsAltTextUnique } = this.props;
        const forbiddenWords = ['PHOTO', 'PICTURE', 'IMAGE'];
        const inputUpperCase = input.toUpperCase();

        const parsed = {
            isValid: true,
            errorText: null
        };

        const isAltTextUnique = getIsAltTextUnique(input, this.props.id);

        if (!isAltTextUnique) {
            parsed.isValid = false;
            parsed.errorText = 'There is already another image that uses this alt text, and this info must be unique for every image'
        }

        forbiddenWords.forEach((word) => {
            if (inputUpperCase.startsWith(word)) {
                parsed.isValid = false;
                parsed.errorText = 'Screen readers inform users that they are hearing the description of an image, so alt text should not include the words "photo", "picture", or "image"'
            }
        });

        return parsed;
    }

    renderPhotoPreview() {
        const { croppedImage, altText, isSavingCrop } = this.state;
        const { desiredWidth, desiredHeight } = this.props;
        const isImageTooSmall = croppedImage.width < desiredWidth || croppedImage.height < desiredHeight;

        return (
            <div className="rec-photo-manager-thumbnail">
                <img
                    className="rec-photo-manager-thumbnail-image"
                    src={croppedImage.source}
                    alt={altText}
                    role="presentation"
                    onLoad={() => this.setState({ isSavingCrop: false })} />
                {isImageTooSmall && (
                    <div className="rec-photo-manager-size-reporter">
                        <div className={cx({ 'rec-photo-manager-error-text': isImageTooSmall })}>
                            Current Dimensions:{' '}
                            <span className="nowrap">
                                <strong>{croppedImage.width}</strong> x{' '}
                                <strong>
                                    {croppedImage.height}
                                    px
                                </strong>
                            </span>
                        </div>
                        <div>
                            Minimum Dimensions:{' '}
                            <span className="nowrap">
                                {desiredWidth} x {desiredHeight}
                                px
                            </span>
                        </div>
                    </div>
                )}

                <div className="rec-photo-manager-thumbnail-actions">
                    <button type="button" onClick={this.openFilesInput} className="rec-photo-manager-text-button">
                        Replace Image
                    </button>
                    &nbsp;
                    <button
                        type="button"
                        onClick={this.editPhotoCrop}
                        className="rec-photo-manager-text-button">
                        Edit Image Crop
                    </button>
                </div>

                {isSavingCrop && (
                    <div className="rec-photo-manager-thumbnail-loading">
                        <Loading />
                    </div>
                )}
            </div>
        );
    }

    renderPhotoUpload() {
        const { desiredWidth, desiredHeight, id, hasHeroImage } = this.props;

        return (
            <div>
                <div className="rec-photo-manager-upload-controls">
                    <Button onClick={this.openFilesInput}>
                        Upload an Image
                    </Button>

                    {id === 'preview' && hasHeroImage && (
                        <Button onClick={this.useHeroImage} isDisabled={!hasHeroImage}>
                            Use Hero Image
                        </Button>
                    )}
                </div>
                <div>
                    <div>
                        <div>
                            Preferred Image Dimensions:{' '}
                            <span className="nowrap">
                                <strong>{desiredWidth}</strong> x <strong>{desiredHeight}</strong>
                                px
                            </span>
                        </div>
                        <div>
                            Accepted file types: <strong>jpg</strong>, <strong>jpeg</strong>, <strong>png</strong>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            imageType,
            imageTypeDisplay,
            id,
            desiredWidth,
            desiredHeight,
            position,
            numberOfGalleryItems
        } = this.props;
        const {
            dropzoneActive,
            originalImage,
            croppedImage,
            altText,
            caption,
            credit,
            isUploading,
            isSaving,
            error,
            isCropping,
            isWarningAboutPromoting,
            isAltTextInvalid
        } = this.state;
        const hasError = error && error.length;
        const isFormInvalid = !croppedImage.source || !altText || !caption || !credit;
        const isFormEmpty = !croppedImage.source && !altText && !caption && !credit;
        const hasFormChanged =
            this.lastSavedState.originalImage.source !== originalImage.source ||
            this.lastSavedState.altText !== altText ||
            this.lastSavedState.caption !== caption ||
            this.lastSavedState.credit !== credit ||
            (croppedImage.mediaId === undefined && !isFormEmpty);

        const isEditingActive = dropzoneActive || hasFormChanged || isUploading || isSaving || hasError;

        if (typeof originalImage === 'object' && originalImage.source) {
            this.imageHasBeenPublished = originalImage.source.indexOf(';base64') === -1;
        }

        return (
            <div className="rec-single-photo-manager">
                <Dropzone
                    disableClick
                    multiple={false}
                    style={{ position: 'relative' }}
                    accept="image/jpeg, image/png"
                    onDrop={this.onDrop}
                    onDragEnter={this.onDragEnter}
                    onDropRejected={this.onDropRejected}
                    onDragLeave={this.onDragLeave}
                    inputProps={{ id: `photo-files-input-${id}`, className: 'rec-photo-manager-files-input' }}>
                    <div className={cx('rec-photo-manager', { active: isEditingActive })}>
                        <form className="rec-photo-manager-form" onSubmit={this.publishChanges}>
                            <div className="rec-photo-manager-left-col">
                                {typeof croppedImage === 'object' && croppedImage.source
                                    ? this.renderPhotoPreview()
                                    : this.renderPhotoUpload()}
                            </div>

                            <div className="rec-photo-manager-right-col">
                                <div className="rec-photo-manager-form-item-block">
                                    <div className="rec-form-item-wrap">
                                        <TextField
                                            key={this.state.originalImage.source}
                                            id={`rec-photo-manager-alt-text-${id}`}
                                            name="altText"
                                            label="Alternative Text:"
                                            isRequired
                                            placeholder="Very short and unique description used by screen readers"
                                            value={altText}
                                            onChange={this.handleInputChange}
                                            validator={this.altTextValidator}
                                            handleNewErrorState={this.handleNewErrorState}
                                            shouldValidateOnMount
                                            shouldValidateOnUpdate />
                                    </div>

                                    <div className="rec-form-item-wrap">
                                        <Label
                                            htmlFor={`rec-photo-manager-caption-${id}`}
                                            isRequired>
                                            Caption Text:
                                        </Label>
                                        <textarea
                                            id={`rec-photo-manager-caption-${id}`}
                                            rows="2"
                                            name="caption"
                                            placeholder="The text displayed under the photo"
                                            value={caption}
                                            onChange={this.handleInputChange} />
                                    </div>

                                    <div className="rec-form-item-wrap">
                                        <TextField
                                            id={`rec-photo-manager-credit-${id}`}
                                            name="credit"
                                            label="Photo Credit:"
                                            isRequired
                                            placeholder="Person/organization who took the photo or owns the rights"
                                            value={credit}
                                            onChange={this.handleInputChange} />
                                    </div>
                                </div>

                                <div className="rec-photo-manager-action-buttons">
                                    <div className="rec-photo-manager-action-buttons-left">
                                        {imageType === 'gallery' && (
                                            <div className="rec-photo-gallery-item-position-btn-wrapper">
                                                <button
                                                    className="rec-button-tertiary rec-photo-gallery-item-position-btn"
                                                    onClick={this.movePositionUp}
                                                    disabled={position === 0 || isEditingActive}
                                                    aria-label="Move position up">
                                                    <Icons.IconChevronUp alt="" />
                                                </button>
                                                <button
                                                    className="rec-button-tertiary rec-photo-gallery-item-position-btn"
                                                    onClick={this.movePositionDown}
                                                    disabled={position === numberOfGalleryItems - 1 || isEditingActive}
                                                    aria-label="Move position down">
                                                    <Icons.IconChevronDown alt="" />
                                                </button>
                                                {position !== -1 && (
                                                    <div className="rec-photo-gallery-position-label">
                                                        Image {position + 1} of {numberOfGalleryItems}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {!hasFormChanged && !isFormEmpty && imageType === 'gallery' && (
                                        <button
                                            type="button"
                                            className="rec-photo-manager-text-button"
                                            onClick={this.toggleWarningAboutPromoting}
                                            disabled={isFormEmpty}>
                                            <Icons.IconFileUpload alt="" /> Promote to Hero Image
                                        </button>
                                    )}
                                    <div className="rec-photo-manager-action-buttons-right">

                                        {!hasFormChanged && !isFormEmpty && (
                                            <button
                                                type="button"
                                                className="rec-photo-manager-text-button"
                                                onClick={this.deletePhotoAndText}
                                                disabled={isFormEmpty}>
                                                <Icons.IconDelete alt="" /> Delete Photo and Text
                                            </button>
                                        )}

                                        {hasFormChanged && (
                                            <button type="button" className="rec-photo-manager-text-button" onClick={this.cancelEditing} disabled={isFormEmpty}>
                                                Cancel Editing
                                            </button>
                                        )}

                                        {(hasFormChanged || isUploading) && (
                                            <button type="submit" className="rec-button-primary" disabled={!hasFormChanged || isFormInvalid || isAltTextInvalid}>
                                                {isFormInvalid ? 'Complete All Fields Before Publishing' : `Publish ${imageTypeDisplay}`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className={cx('rec-photo-manager-overlay', 'rec-photo-dropzone-overlay', { active: dropzoneActive })}>
                            <Icons.IconCloudUpload />
                            <h3>Drop File To Upload a New {imageTypeDisplay}</h3>
                        </div>

                        <div className={cx('rec-photo-manager-overlay', { active: isUploading })}>
                            <UploadProgressIndicator
                                name={`Publishing ${imageTypeDisplay}`}
                                status={this.state.uploadStatus}
                                progress={this.state.uploadPercentCompleted} />
                        </div>

                        <div className={cx('rec-photo-manager-overlay', { active: isSaving })}>
                            <Loading />
                        </div>

                        <div className={cx('rec-photo-manager-overlay', 'rec-photo-manager-error-overlay', { active: error && error.length })}>
                            <div className="rec-photo-manager-error-icon">
                                <Icons.IconError />
                            </div>
                            <h3>
                                Sorry, there was an error
                            </h3>
                            <p>{error}</p>
                            <Button onClick={this.clearError}>
                                OK
                            </Button>
                        </div>
                    </div>
                </Dropzone>

                {isCropping && (
                    <Modal
                        role="complementary"
                        isOpen={isCropping}
                        className="rec-photo-crop-modal"
                        overlayClassName="rec-overlay-base rec-overlay-after-open"
                        onRequestClose={this.cancelCrop}>
                        <h2>{imageTypeDisplay} Crop</h2>
                        <PhotoCropper
                            aspect={id === 'hero' ? 72 / 17 : 4 / 3}
                            width={originalImage.width}
                            height={originalImage.height}
                            desiredWidth={desiredWidth}
                            desiredHeight={desiredHeight}
                            uploadImageSource={originalImage.source}
                            saveCroppedPhoto={this.saveCroppedPhoto}
                            cancelCrop={this.cancelCrop}
                            disabled={this.state.saveButtonDisabled}
                            previousCrop={this.state.croppedImage.percentCrop}
                            isFreeform={imageType === 'gallery'}
                            imageHasBeenPublished={this.imageHasBeenPublished} />
                    </Modal>
                )}

                {isWarningAboutPromoting && (
                    <Modal
                        role="complementary"
                        isOpen={isWarningAboutPromoting}
                        className="rec-photo-help-modal"
                        overlayClassName="rec-overlay-base rec-overlay-after-open"
                        onRequestClose={this.toggleWarningAboutPromoting}>
                        <PromoteGalleryToHero
                            mediaId={croppedImage.mediaId}
                            uploadManager={this.props.uploadManager}
                            toggleWarningAboutPromoting={this.toggleWarningAboutPromoting}
                            onHeroPromotion={this.props.onHeroPromotion} />
                    </Modal>
                )}

                <hr />
            </div>
        );
    }
}
